import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles, TrendingUp, AlertTriangle, Clock, Target } from "lucide-react";
import { motion } from "framer-motion";

export type AIInsight = {
  icon: typeof TrendingUp | typeof AlertTriangle | typeof Clock | typeof Target;
  title: string;
  description: string;
  type: "success" | "warning" | "info";
};

interface AIInsightsCardProps {
  insights: AIInsight[];
}

export function AIInsightsCard({ insights }: AIInsightsCardProps) {
  const typeColors = {
    success: "text-green-500",
    warning: "text-amber-500",
    info: "text-blue-500",
  };

  const typeBgColors = {
    success: "bg-green-500/10",
    warning: "bg-amber-500/10",
    info: "bg-blue-500/10",
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 }}
    >
      <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20 backdrop-blur-xl">
        <CardHeader className="p-4 sm:p-6">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-primary animate-pulse" />
            </div>
            <CardTitle className="text-base sm:text-lg">Insights da IA</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="space-y-2 sm:space-y-3">
            {insights.map((insight, index) => {
              const IconComponent = insight.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className={`flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg ${typeBgColors[insight.type]} hover:bg-primary/5 transition-colors cursor-pointer`}
                >
                  <div className={`mt-0.5 flex-shrink-0 ${typeColors[insight.type]}`}>
                    <IconComponent className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-xs sm:text-sm">{insight.title}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1">{insight.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
